import React, { useMemo, useState, useEffect } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { IN_DEPTH_SECTION_LABELS } from '../utils/inDepthAnalysisSections';
import {
  allTabFields,
  allTabsPayload,
  editorStateFrom,
  hasAtGlanceChanges,
  tabFields,
  tabPayload,
  validateTab,
} from '../utils/reportEditorState';
import InDepthStructuredEditor from './InDepthStructuredEditor';
import {
  AtGlanceForm,
  LegacyAnalysisForm,
  MarkdownSectionEditor,
  SpeakersForm,
  TabStatusIcon,
} from './ReportEditorSections';

const NO_CHANGES = { at_glance: false, full_report: false, speakers: false, analysis: false };

const TABS = [
  { id: 'at_glance', label: 'Session at a glance' },
  { id: 'full_report', label: 'Full report' },
  { id: 'speakers', label: 'Speakers' },
  { id: 'analysis', label: 'In-depth analysis' },
];

const TAB_STATUS_TEXT = {
  unsaved: () => 'You have unsaved changes',
  saved: () => 'All changes saved',
  error: (message) => `Error: ${message || 'Failed to save'}`,
  saving: () => 'Saving...',
};

const EMPTY_SPEAKER = { name: '', arguments: [] };
const emptyArgument = () => ({ name: '', explanation: '', evidence: '', majorDiscussionPoint: '' });

const saveButtonClass = (enabled) => `
              px-4 py-2 text-sm font-medium rounded-lg transition-colors
              ${enabled
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }
            `;

/** Report editing with one tab per report section. */
const ReportEditor = ({
  sessionId,
  sessionTitle,
  initialData = {},
  onSave,
  onCancel,
  isSaving = false,
  saveErrors: externalSaveErrors = {},
}) => {
  const [activeTab, setActiveTab] = useState('at_glance');

  const [data, setData] = useState(() => editorStateFrom(initialData));
  const [savedStates, setSavedStates] = useState(data);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(NO_CHANGES);
  const [validationErrors, setValidationErrors] = useState({});

  const mergedErrors = useMemo(
    () => ({ ...(externalSaveErrors || {}), ...(validationErrors || {}) }),
    [externalSaveErrors, validationErrors]
  );

  // initialData changes after the report loads from the API
  useEffect(() => {
    const next = editorStateFrom(initialData);
    setData(next);
    setSavedStates(next);
    setValidationErrors({});
    setHasUnsavedChanges(NO_CHANGES);
  }, [initialData]);

  const markUnsaved = (tabId, unsaved = true) => {
    setHasUnsavedChanges((prev) => ({ ...prev, [tabId]: unsaved }));
  };

  const handleTextChange = (key, value) => {
    const next = { ...data, [key]: value || '' };
    setData((prev) => ({ ...prev, [key]: value || '' }));
    if (key.startsWith('at_glance_')) {
      markUnsaved('at_glance', hasAtGlanceChanges(next, savedStates));
    } else if (key === 'full_report_md') {
      markUnsaved('full_report', (value || '') !== (savedStates.full_report_md || ''));
    }
  };

  const handleAnalysisSectionChange = (sectionKey, value) => {
    setData((prev) => ({
      ...prev,
      analysis_sections: { ...(prev.analysis_sections || {}), [sectionKey]: value },
    }));
    markUnsaved('analysis', IN_DEPTH_SECTION_LABELS.some(({ key }) => {
      const newVal = key === sectionKey ? value : (data.analysis_sections?.[key] || '');
      return (newVal || '') !== (savedStates.analysis_sections?.[key] || '');
    }));
  };

  const handleAnalysisDictChange = (newDict) => {
    setData((prev) => ({ ...prev, analysis_dict: newDict }));
    markUnsaved('analysis', JSON.stringify(newDict) !== JSON.stringify(savedStates.analysis_dict));
  };

  /** Apply `update` to a copy of the speakers list; speaker objects are replaced, never mutated. */
  const updateSpeakers = (update) => {
    setData((prev) => ({ ...prev, speakers_data: update([...(prev.speakers_data || [])]) }));
    markUnsaved('speakers');
  };

  const speakerHandlers = {
    addSpeaker: () => updateSpeakers((list) => [...list, { ...EMPTY_SPEAKER }]),
    removeSpeaker: (i) => updateSpeakers((list) => list.filter((_, index) => index !== i)),
    updateSpeaker: (i, field, value) => updateSpeakers((list) => {
      list[i] = { ...(list[i] || EMPTY_SPEAKER), [field]: value };
      return list;
    }),
    addArgument: (i) => updateSpeakers((list) => {
      const speaker = list[i] || EMPTY_SPEAKER;
      list[i] = { ...speaker, arguments: [...(speaker.arguments || []), emptyArgument()] };
      return list;
    }),
    removeArgument: (i, j) => updateSpeakers((list) => {
      if (list[i]?.arguments) {
        list[i] = { ...list[i], arguments: list[i].arguments.filter((_, index) => index !== j) };
      }
      return list;
    }),
    updateArgument: (i, j, field, value) => updateSpeakers((list) => {
      const speaker = list[i] || EMPTY_SPEAKER;
      const args = [...(speaker.arguments || [])];
      args[j] = { ...(args[j] || emptyArgument()), [field]: value };
      list[i] = { ...speaker, arguments: args };
      return list;
    }),
  };

  const handleSave = async (tabId) => {
    const errors = validateTab(tabId, data);
    if (errors.length > 0) {
      setValidationErrors((prev) => ({ ...prev, [tabId]: errors.join('; ') }));
      return;
    }
    try {
      await onSave(tabPayload(tabId, data));
      setSavedStates((prev) => ({ ...prev, ...tabFields(tabId, data) }));
      markUnsaved(tabId, false);
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const hasAnyUnsavedChanges = Object.values(hasUnsavedChanges).some(Boolean);

  const handleSaveAll = async () => {
    const allErrors = {};
    for (const { id } of TABS) {
      const errors = validateTab(id, data);
      if (errors.length > 0) allErrors[id] = errors.join('; ');
    }
    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      return;
    }
    if (!TABS.some((tab) => hasUnsavedChanges[tab.id])) return;

    try {
      await onSave(allTabsPayload(data));
      setSavedStates((prev) => ({ ...prev, ...allTabFields(data) }));
      setHasUnsavedChanges(NO_CHANGES);
    } catch (error) {
      console.error('Save all failed:', error);
    }
  };

  const handleCancel = (tabId) => {
    setData((prev) => ({ ...prev, ...tabFields(tabId, savedStates) }));
    markUnsaved(tabId, false);
  };

  const getTabStatus = (tabId) => {
    if (isSaving) return 'saving';
    if (mergedErrors[tabId]) return 'error';
    if (hasUnsavedChanges[tabId]) return 'unsaved';
    return 'saved';
  };

  const renderTabForm = (tabId) => {
    if (tabId === 'at_glance') return <AtGlanceForm data={data} onChange={handleTextChange} />;
    if (tabId === 'speakers') return <SpeakersForm speakers={data.speakers_data} handlers={speakerHandlers} />;
    if (tabId === 'analysis') {
      // A structured dict is edited in the structure-preserving form so it stays a dict.
      return data.analysis_dict && typeof data.analysis_dict === 'object' ? (
        <InDepthStructuredEditor value={data.analysis_dict} onChange={handleAnalysisDictChange} />
      ) : (
        <LegacyAnalysisForm sections={data.analysis_sections} onChange={handleAnalysisSectionChange} />
      );
    }
    return (
      <MarkdownSectionEditor
        tabId={tabId}
        value={data.full_report_md}
        onChange={(value) => handleTextChange('full_report_md', value)}
      />
    );
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onCancel}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Session Details
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{sessionTitle || 'Report Editor'}</h1>
              <p className="text-sm text-gray-500 mt-1">Session ID: {sessionId}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200">
        <div className="px-6">
          <div className="flex space-x-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                    px-4 py-3 text-sm font-medium border-b-2 transition-colors
                    ${activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
              >
                <div className="flex items-center gap-2">
                  <span>{tab.label}</span>
                  <TabStatusIcon status={getTabStatus(tab.id)} />
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {hasAnyUnsavedChanges ? (
              <span>You have unsaved changes across multiple tabs</span>
            ) : (
              <span className="text-green-600">All changes saved</span>
            )}
          </div>
          <button
            onClick={handleSaveAll}
            disabled={isSaving || !hasAnyUnsavedChanges}
            className={saveButtonClass(hasAnyUnsavedChanges && !isSaving)}
          >
            {isSaving ? 'Saving All...' : 'Save All'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {TABS.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? 'block' : 'hidden'}
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{tab.label}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {TAB_STATUS_TEXT[getTabStatus(tab.id)](mergedErrors[tab.id])}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {hasUnsavedChanges[tab.id] && (
                    <button
                      onClick={() => handleCancel(tab.id)}
                      disabled={isSaving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={() => handleSave(tab.id)}
                    disabled={isSaving || !hasUnsavedChanges[tab.id]}
                    className={saveButtonClass(hasUnsavedChanges[tab.id] && !isSaving)}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              {mergedErrors[tab.id] && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{mergedErrors[tab.id]}</p>
                </div>
              )}

              <div data-color-mode="light" className="mt-4">
                {renderTabForm(tab.id)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportEditor;
